export default {
  title: 'Teleconsultation',
  subtitle: 'Consult an expert doctor in a few minutes, wherever you are.',
  errorStart: 'Error starting the consultation',
  doctorWaiting: '{{name}} is waiting for you',
  defaultDoctor: 'The doctor',
  newCare: {
    title: 'New Care',
    description: 'Immediate start of a secure video or audio consultation.',
    action: 'Start now'
  },
  availability: {
    title: 'Availability',
    doctorAvailable: 'Doctor available',
    activeQueue: 'Active queue',
    estimatedWait: 'Estimated wait: 5-10 min',
    support24h: '24/7 medical guard'
  },
  techStandard: {
    title: 'EKOSE-RX Technology Standard',
    videoHd: 'HD Video',
    videoHdDesc: 'Optimized stream',
    secureChat: 'Secure Chat',
    secureChatDesc: 'Encrypted exchanges',
    hdsArchiving: 'HDS Archiving',
    hdsArchivingDesc: 'Patient monitoring'
  },
  form: {
    title: 'New',
    titleHighlight: 'Teleconsultation',
    subtitle: 'Describe your symptoms to be connected with a doctor',
    typeLabel: 'Consultation type',
    videoType: 'HD Video',
    audioType: 'Audio Only',
    symptomsLabel: 'Your Symptoms',
    symptomsPlaceholder: 'Describe your symptoms, their duration, intensity...',
    doctorAvailableDesc: 'Direct connection available',
    queueDesc: 'Connection shortly',
    queueLabel: 'Waiting line',
    cancel: 'Cancel',
    submit: 'Start consultation',
    starting: 'Starting...'
  },
  waitingRoom: {
    patient: {
      loadingTitle: 'Connection',
      loadingTitleHighlight: 'Elite',
      loadingSubtitle: 'Securing HDS tunnel...',
      welcomeTitle: 'Welcome',
      welcomeSubtitle: 'Private Medical Space',
      liveSession: 'Live Session',
      hdsMedicalGrade: 'HDS Medical Grade',
      roomTitle: 'Waiting Room',
      roomTitleHighlight: 'Elite',
      syncActive: 'Sync Active',
      reconnecting: 'Reconnecting...',
      cancel: 'Cancel',
      synchronized: 'Synchronized',
      prepareConsultation: 'Prepare for the consultation',
      priorityRoom: 'Priority Waiting Room',
      encryptedSession: 'Your session is end-to-end encrypted. The Doctor has received your presence alert.',
      keepWindowOpen: 'Keep this window open for auto-connection.',
      position: 'Position',
      estimatedWait: 'Estimated Wait',
      min: 'min',
      eliteConfidentiality: 'Elite Confidentiality',
      encryptedFlows: 'AES-256 encrypted flows. No data is stored outside the HDS tunnel.',
      optimizedSignal: 'Optimized Signal',
      autoQuality: 'EKOSE automatically adjusts video quality based on your network.',
      syncError: 'Synchronization Error',
      chatLive: 'Live Chat',
      online: 'Online',
      chatUnavailable: 'Chat Unavailable'
    },
    doctor: {
      realTime: 'Real Time',
      systemActive: 'System Active',
      offline: 'Offline',
      roomTitle: 'Waiting Room',
      waiting: 'waiting',
      available: 'Available',
      busy: 'Busy',
      statWaiting: 'Waiting',
      statUrgent: 'Urgent',
      statAverage: 'Average',
      statMessages: 'Messages',
      interactiveQueue: 'Interactive Queue',
      sortedByUrgency: 'Patients sorted by urgency level',
      loading: 'Loading...',
      noPatients: 'No patients waiting',
      noPatientsDesc: 'The waiting room is empty. You will be notified when a patient arrives.',
      symptomsNotSpecified: 'Symptoms not specified',
      chat: 'Chat',
      treat: 'Treat',
      directLink: 'Direct link: {{name}}',
      statusAvailable: 'Available status',
      statusAvailableDesc: 'You receive new consultations automatically',
      patientSupport: 'Patient support',
      patientSupportDesc: 'Answer patient questions via integrated chat',
      quickConsultation: 'Quick consultation',
      quickConsultationDesc: 'Start a teleconsultation in one click'
    }
  },
  videoCall: {
    initializationEkose: "EKOSE Initialization",
    securingTunnel: "Securing the consultation tunnel in progress...",
    streamError: "Stream Error",
    retryConnection: "Retry Connection",
    cameraDisabled: "Camera disabled",
    audioOnlySecured: "Audio stream only • Secured",
    patientApproaching: "Patient approaching...",
    practitionerArriving: "Practitioner arriving...",
    patientFinalizingConnection: "The patient is finalizing their secure connection. Video will display automatically.",
    doctorJoiningConsultation: "Your doctor is joining the consultation. Prepare your questions.",
    cacheEnabled: "Privacy filter active",
    you: "You",
    ekoseProtectedLine: "EKOSE PROTECTED LINE"
  },
  errors: {
    loadFailed: 'Error loading the consultation.',
    availabilityFailed: 'Error retrieving doctor availability.',
    unauthenticated: 'User not authenticated.',
    cannotStart: 'Cannot start a consultation in this state.',
    alreadyInProgress: 'A consultation is already in progress. Please join or cancel it.',
    startFailed: 'Error starting the consultation.',
    invalidSession: 'Invalid consultation session.',
    cancelFailed: 'Error cancelling the consultation.',
    joinQueueFailed: 'Unable to join the waiting room.',
    unauthenticatedOrDisconnected: 'User not authenticated or disconnected.',
    endFailed: 'An error occurred while ending the consultation.',
    cancelled: 'Your consultation has been cancelled.',
    messageSendFailed: 'Unable to send the message. Please try again.'
  },
  waitingRoomService: {
    positionInQueue: 'You are at position {{position}} in the queue',
    patientJoined: '{{name}} just joined the waiting room',
    welcomeToast: '🏥 You are in the waiting room (position {{position}})',
    positionImproved: '🎉 Your position improved! You are now {{position}}{{suffix}}',
    positionCurrent: 'Your position in queue: {{position}}{{suffix}}',
    waitTimeReduced: '⚡ Wait time reduced by {{diff}} minutes! Estimate: {{time}} min',
    waitTimeUpdated: 'Wait time updated: {{time}} minutes',
    doctorAssigned: '👨‍⚕️ Doctor assigned: Dr. {{name}}',
    doctorAssignedDefault: '👨‍⚕️ Doctor assigned',
    doctorAssignedSpecialty: ' ({{specialty}})',
    statusDoctorAssigned: '✅ A doctor has been assigned to you',
    statusConsultationStarting: '🚀 Your consultation is about to begin!',
    statusCancelled: '❌ Consultation cancelled',
    delayDetected: '⏰ Delay detected: +{{minutes}} minutes over the estimate',
    delayToast: '⏰ Your consultation is taking a bit longer than expected',
    progress: '📊 Progress: {{percent}}% of estimated wait time',
    noInfo: 'No information available',
    summaryPosition: '📍 Position: {{position}}{{suffix}}',
    summaryWaitTime: '⏱️ Wait time: {{current}}/{{estimated}} min',
    summaryDoctor: '👨‍⚕️ Doctor: Dr. {{name}}',
    summaryStatus: '📊 Status: {{status}}',
    statusLabels: {
      waiting: 'Waiting',
      doctorAssigned: 'Doctor assigned',
      consultationStarting: 'Consultation starting',
      cancelled: 'Cancelled',
      unknown: 'Unknown'
    },
    positionSuffix: {
      first: 'st',
      other: 'th'
    }
  }
};

